/**
 * Parsowanie pola `notes` dokumentu Stage3 (JSON w jednej kolumnie `notes`, typ text):
 * - viewer_page_rotations: strona → dodatkowy obrót 0 | 90 | 180 | 270 (wobec /Rotate PDF)
 * - comment_overlay_offset_y / comment_overlay_offset_x: px
 * - comment_overlay_scale: { x, y } — skala % od środka (np. 70, 150)
 * - comment_overlay_rotation_deg: obrót tylko warstwy HTML
 * - hide_overlay_highlight_boxes: ukryj żółte prostokąty (lista komentarzy zostaje)
 * - hide_pdf_annotation_render: wyłącz rysowanie adnotacji PDF na canvas (pdf.js)
 *
 * Wszystko w jednej kolumnie `notes` (TEXT).
 */

export type CommentOverlayOffsetParsed = {
	byPage: Record<number, number>;
	global: number | null;
};

const ALLOWED_ROT = new Set([0, 90, 180, 270]);

/** Rozparsuj `notes` z API (string JSON lub już obiekt). */
export function parseNotesObject(raw: unknown): Record<string, unknown> | null {
	if (raw == null || raw === '') {
		return null;
	}
	if (typeof raw === 'object' && !Array.isArray(raw)) {
		return raw as Record<string, unknown>;
	}
	const s = String(raw).trim();
	if (s === '' || s === '[object Object]') {
		return null;
	}
	try {
		const p = JSON.parse(s);
		return typeof p === 'object' && p !== null && !Array.isArray(p) ? (p as Record<string, unknown>) : null;
	} catch {
		return null;
	}
}

export function normalizeViewerRotationDeg(value: unknown): 0 | 90 | 180 | 270 {
	const n = Number(value);
	if (!Number.isFinite(n)) {
		return 0;
	}
	const mod = ((Math.round(n) % 360) + 360) % 360;
	const snapped = (((Math.round(mod / 90) * 90) % 360) + 360) % 360;
	return (ALLOWED_ROT.has(snapped) ? snapped : 0) as 0 | 90 | 180 | 270;
}

export function parseViewerPageRotations(notesRawInput: unknown): Record<number, number> {
	const parsed = parseNotesObject(notesRawInput);
	if (!parsed) {
		return {};
	}
	const map = parsed.viewer_page_rotations;
	if (!map || typeof map !== 'object' || Array.isArray(map)) {
		return {};
	}
	const result: Record<number, number> = {};
	Object.entries(map as Record<string, unknown>).forEach(([k, v]) => {
		const page = Number(k);
		const rot = normalizeViewerRotationDeg(v);
		if (Number.isFinite(page) && page > 0 && rot !== 0) {
			result[page] = rot;
		}
	});
	return result;
}

export function parseCommentOverlayOffsetY(notesRawInput: unknown): CommentOverlayOffsetParsed {
	const parsed = parseNotesObject(notesRawInput);
	if (!parsed) {
		return { byPage: {}, global: null };
	}
	const raw = parsed.comment_overlay_offset_y;
	if (raw === undefined || raw === null) {
		return { byPage: {}, global: null };
	}
	if (typeof raw === 'number' && Number.isFinite(raw)) {
		return { byPage: {}, global: raw };
	}
	if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
		const byPage: Record<number, number> = {};
		Object.entries(raw).forEach(([k, v]) => {
			const page = Number(k);
			const px = Number(v);
			if (Number.isFinite(page) && page > 0 && Number.isFinite(px)) {
				byPage[page] = px;
			}
		});
		return { byPage, global: null };
	}
	return { byPage: {}, global: null };
}

export function parseCommentOverlayOffsetX(notesRawInput: unknown): CommentOverlayOffsetParsed {
	const parsed = parseNotesObject(notesRawInput);
	if (!parsed) {
		return { byPage: {}, global: null };
	}
	const raw = parsed.comment_overlay_offset_x;
	if (raw === undefined || raw === null) {
		return { byPage: {}, global: null };
	}
	if (typeof raw === 'number' && Number.isFinite(raw)) {
		return { byPage: {}, global: raw };
	}
	if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
		const byPage: Record<number, number> = {};
		Object.entries(raw).forEach(([k, v]) => {
			const page = Number(k);
			const px = Number(v);
			if (Number.isFinite(page) && page > 0 && Number.isFinite(px)) {
				byPage[page] = px;
			}
		});
		return { byPage, global: null };
	}
	return { byPage: {}, global: null };
}

/** Skala prostokątów komentarzy od środka strony (100 = 100%). */
export function parseCommentOverlayScale(notesRawInput: unknown): { x: number; y: number } {
	const parsed = parseNotesObject(notesRawInput);
	if (!parsed) {
		return { x: 100, y: 100 };
	}
	const raw = parsed.comment_overlay_scale;
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
		return { x: 100, y: 100 };
	}
	const o = raw as Record<string, unknown>;
	const x = Number(o.x);
	const y = Number(o.y);
	return {
		x: Number.isFinite(x) && x > 0 ? x : 100,
		y: Number.isFinite(y) && y > 0 ? y : 100,
	};
}

export function parseHideOverlayHighlightBoxes(notesRawInput: unknown): boolean {
	const parsed = parseNotesObject(notesRawInput);
	if (!parsed) {
		return false;
	}
	return Boolean(parsed.hide_overlay_highlight_boxes);
}

export function parseHidePdfAnnotationRender(notesRawInput: unknown): boolean {
	const parsed = parseNotesObject(notesRawInput);
	if (!parsed) {
		return false;
	}
	return Boolean(parsed.hide_pdf_annotation_render);
}

/** Parsuje "70,150" lub "70;150" → { x: 70, y: 150 } */
export function parseScalePromptInput(raw: string): { x: number; y: number } | null {
	const s = String(raw || '').trim();
	if (s === '') {
		return { x: 100, y: 100 };
	}
	const parts = s
		.split(/[,;]\s*/)
		.map((p) => p.trim())
		.filter(Boolean);
	if (parts.length === 1) {
		const n = Number(parts[0]);
		if (!Number.isFinite(n) || n <= 0) {
			return null;
		}
		return { x: n, y: n };
	}
	if (parts.length >= 2) {
		const x = Number(parts[0]);
		const y = Number(parts[1]);
		if (!Number.isFinite(x) || !Number.isFinite(y) || x <= 0 || y <= 0) {
			return null;
		}
		return { x, y };
	}
	return null;
}

/** Osobna rotacja warstwy komentarzy (ten sam format co viewer_page_rotations). */
export function parseCommentOverlayRotationDeg(notesRawInput: unknown): Record<number, number> {
	const parsed = parseNotesObject(notesRawInput);
	if (!parsed) {
		return {};
	}
	const map = parsed.comment_overlay_rotation_deg;
	if (!map || typeof map !== 'object' || Array.isArray(map)) {
		return {};
	}
	const result: Record<number, number> = {};
	Object.entries(map as Record<string, unknown>).forEach(([k, v]) => {
		const page = Number(k);
		const rot = normalizeViewerRotationDeg(v);
		if (Number.isFinite(page) && page > 0 && rot !== 0) {
			result[page] = rot;
		}
	});
	return result;
}

/**
 * Format z promptu: "1:90,2:180" lub legacy "2,4,7" (= strony z obrotem 180°).
 * Obsługa dwukropka ASCII i unicode (np. wklejone z Word).
 */
export function parseViewerSoftRotationInput(raw: string): Record<number, number> {
	const result: Record<number, number> = {};
	const tokens = String(raw || '')
		.split(',')
		.map((t) => t.trim())
		.filter(Boolean);
	for (const token of tokens) {
		const colonIdx = token.search(/[:：]/);
		if (colonIdx !== -1) {
			const pPart = token.slice(0, colonIdx).trim();
			const dRaw = token
				.slice(colonIdx + 1)
				.trim()
				.replace(/°/g, '');
			const page = Number(pPart);
			const degNum = Number(String(dRaw).replace(/[^\d.-]/g, ''));
			const deg = normalizeViewerRotationDeg(degNum);
			if (Number.isFinite(page) && page > 0 && deg !== 0) {
				result[Math.trunc(page)] = deg;
			}
		} else {
			const page = Number(token.replace(/[^\d]/g, ''));
			if (Number.isFinite(page) && page > 0) {
				result[Math.trunc(page)] = 180;
			}
		}
	}
	return result;
}

export function parseCommentSoftRotationInput(raw: string): Record<number, number> {
	return parseViewerSoftRotationInput(raw);
}
